import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Send,
  Archive,
  Save,
  Trash2,
  ArrowUp,
  ArrowDown,
  BarChart3,
  LayoutGrid,
  Sliders,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  storiesAdminService,
  DEFAULT_SLIDE,
  GRADIENT_PRESETS,
  type Story,
  type StorySlide,
  type StoryStats,
  type StoryStatus,
  type StoryVariablesRegistry,
  type TargetAudience,
  type UpdateSlidePayload,
  type UpdateStoryPayload,
} from '../../services/storiesAdminService';
import StoryCardPreview from './components/StoryCardPreview';
import StorySlideEditor from './components/StorySlideEditor';
import StoryAnalyticsPanel from './components/StoryAnalyticsPanel';

type Tab = 'content' | 'meta' | 'analytics';

const StoryEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const storyId = parseInt(id || '0', 10);

  const [story, setStory] = useState<Story | null>(null);
  const [slides, setSlides] = useState<StorySlide[]>([]);
  const [varsRegistry, setVarsRegistry] = useState<StoryVariablesRegistry | null>(null);
  const [selectedSlideId, setSelectedSlideId] = useState<number | null>(null);
  const [editedSlide, setEditedSlide] = useState<StorySlide | null>(null);
  const [slideDirty, setSlideDirty] = useState(false);
  const [savingSlide, setSavingSlide] = useState(false);

  const [stats, setStats] = useState<StoryStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [storyDirty, setStoryDirty] = useState(false);
  const [savingStory, setSavingStory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('content');

  // Доступные переменные для autocomplete в text-полях текущего слайда.
  // = объединение Variables всех выбранных story.data_sources + ItemVars
  // источника, на который указывает slide.iterate_over (`events.upcoming` → `events`).
  const availableVarsForSlide = useMemo(() => {
    if (!varsRegistry || !story || story.kind !== 'system') return [];
    const ds = story.data_sources || [];
    if (ds.length === 0) return [];
    const fromSources = varsRegistry.sources
      .filter((s) => ds.includes(s.id))
      .flatMap((s) => s.variables);
    // Если у текущего слайда iterate_over — добавляем itemVars из соответствующего источника.
    const it = editedSlide?.iterate_over;
    const itemVars = it
      ? varsRegistry.sources
          .filter((s) => it.startsWith(s.id + '.'))
          .flatMap((s) => s.itemVars || [])
      : [];
    return [...fromSources, ...itemVars];
  }, [varsRegistry, story, editedSlide]);

  const loadStory = useCallback(async () => {
    if (!storyId) return;
    try {
      setLoading(true);
      const data = await storiesAdminService.get(storyId);
      if (!data) {
        toast.error('История не найдена');
        navigate('/stories');
        return;
      }
      setStory(data);
      const list = (data.slides || []).slice().sort((a, b) => a.position - b.position);
      setSlides(list);
      if (list.length > 0) {
        const keep = selectedSlideId && list.find((s) => s.id === selectedSlideId);
        const target = keep || list[0];
        setSelectedSlideId(target.id);
        setEditedSlide(target);
      } else {
        setSelectedSlideId(null);
        setEditedSlide(null);
      }
      setSlideDirty(false);
      setStoryDirty(false);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка загрузки');
      navigate('/stories');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, navigate]);

  const loadStats = useCallback(async () => {
    if (!storyId) return;
    setStatsLoading(true);
    try {
      const data = await storiesAdminService.stats(storyId);
      setStats(data);
    } finally {
      setStatsLoading(false);
    }
  }, [storyId]);

  useEffect(() => {
    loadStory();
  }, [loadStory]);

  useEffect(() => {
    if (tab === 'analytics') loadStats();
  }, [tab, loadStats]);

  useEffect(() => {
    storiesAdminService.variables().then(setVarsRegistry).catch(() => setVarsRegistry(null));
  }, []);

  // ── Story-level updates (title, status, audience, gradient) ─────────────
  const updateStoryLocal = (patch: Partial<Story>) => {
    setStory((prev) => (prev ? { ...prev, ...patch } : prev));
    setStoryDirty(true);
  };

  const handleSaveStory = async () => {
    if (!story) return;
    setSavingStory(true);
    try {
      const payload: UpdateStoryPayload = {
        slug: story.slug,
        title: story.title,
        cover_emoji: story.cover_emoji ?? null,
        cover_gradient: story.cover_gradient,
        card_text_color: story.card_text_color,
        target_audience: story.target_audience,
        kind: story.kind,
        preset_id: story.preset_id ?? null,
        set_preset_id_null: !story.preset_id,
        data_sources: story.data_sources ?? [],
        refresh_strategy: story.refresh_strategy,
        // Scheduling: published_at — отложенный публиш (story невидим до этой даты),
        // expires_at — story исчезает после (для announcement-stories).
        // ISO-строки (datetime-local input → ISO в onChange handler).
        published_at: story.published_at,
        set_published_at_null: !story.published_at,
        expires_at: story.expires_at,
        set_expires_at_null: !story.expires_at,
      };
      const updated = await storiesAdminService.update(story.id, payload);
      setStory((prev) => (prev ? { ...prev, ...updated } : updated));
      setStoryDirty(false);
      toast.success('История сохранена');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSavingStory(false);
    }
  };

  const handlePublish = async () => {
    if (!story) return;
    try {
      const updated = await storiesAdminService.publish(story.id);
      setStory((prev) => (prev ? { ...prev, ...updated } : updated));
      toast.success('История опубликована');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка');
    }
  };

  const handleArchive = async () => {
    if (!story) return;
    try {
      const updated = await storiesAdminService.archive(story.id);
      setStory((prev) => (prev ? { ...prev, ...updated } : updated));
      toast.success('История в архиве');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка');
    }
  };

  const handleDeleteStory = async () => {
    if (!story) return;
    if (!window.confirm(`Удалить историю «${story.title}»? Все слайды удалятся.`)) return;
    try {
      await storiesAdminService.remove(story.id);
      toast.success('История удалена');
      navigate('/stories');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка');
    }
  };

  // ── Slide-level updates ────────────────────────────────────────────────
  const handleSelectSlide = (slideId: number) => {
    if (slideDirty) {
      if (!window.confirm('У текущего слайда есть несохранённые правки. Переключиться?')) return;
    }
    const s = slides.find((x) => x.id === slideId);
    if (!s) return;
    setSelectedSlideId(slideId);
    setEditedSlide(s);
    setSlideDirty(false);
  };

  const handleSlideChange = (patch: UpdateSlidePayload) => {
    if (!editedSlide) return;
    setEditedSlide({ ...editedSlide, ...patch } as StorySlide);
    setSlideDirty(true);
  };

  const handleSaveSlide = async () => {
    if (!editedSlide || !story) return;
    setSavingSlide(true);
    try {
      const payload: UpdateSlidePayload = {
        position: editedSlide.position,
        duration_ms: editedSlide.duration_ms,
        headline: editedSlide.headline,
        background_gradient: editedSlide.background_gradient,
        text_color: editedSlide.text_color,
        headline_font: editedSlide.headline_font,
      };
      // Nullable fields: send value OR set_x_null
      if (editedSlide.eyebrow != null && editedSlide.eyebrow !== '') {
        payload.eyebrow = editedSlide.eyebrow;
      } else {
        payload.set_eyebrow_null = true;
      }
      if (editedSlide.body != null && editedSlide.body !== '') {
        payload.body = editedSlide.body;
      } else {
        payload.set_body_null = true;
      }
      if (editedSlide.image_url != null && editedSlide.image_url !== '') {
        payload.image_url = editedSlide.image_url;
      } else {
        payload.set_image_url_null = true;
      }
      if (editedSlide.cta_label != null && editedSlide.cta_label !== '') {
        payload.cta_label = editedSlide.cta_label;
      } else {
        payload.set_cta_label_null = true;
      }
      if (editedSlide.cta_url != null && editedSlide.cta_url !== '') {
        payload.cta_url = editedSlide.cta_url;
      } else {
        payload.set_cta_url_null = true;
      }

      const updated = await storiesAdminService.updateSlide(story.id, editedSlide.id, payload);
      setSlides((prev) =>
        prev.map((s) => (s.id === editedSlide.id ? { ...s, ...updated } : s)),
      );
      setEditedSlide((prev) => (prev ? { ...prev, ...updated } : prev));
      setSlideDirty(false);
      toast.success('Слайд сохранён');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка сохранения слайда');
    } finally {
      setSavingSlide(false);
    }
  };

  const handleAddSlide = async () => {
    if (!story) return;
    try {
      const created = await storiesAdminService.addSlide(story.id, {
        ...DEFAULT_SLIDE,
        headline: `Слайд ${slides.length + 1}`,
      });
      if (created) {
        const newList = [...slides, created].sort((a, b) => a.position - b.position);
        setSlides(newList);
        setSelectedSlideId(created.id);
        setEditedSlide(created);
        setSlideDirty(false);
        toast.success('Слайд добавлен');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка добавления слайда');
    }
  };

  const handleDeleteSlide = async (slideId: number) => {
    if (!story) return;
    if (!window.confirm('Удалить слайд?')) return;
    try {
      await storiesAdminService.deleteSlide(story.id, slideId);
      const newList = slides.filter((s) => s.id !== slideId);
      setSlides(newList);
      if (selectedSlideId === slideId) {
        const next = newList[0] || null;
        setSelectedSlideId(next?.id ?? null);
        setEditedSlide(next);
        setSlideDirty(false);
      }
      toast.success('Слайд удалён');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Ошибка удаления');
    }
  };

  const handleMoveSlide = async (slideId: number, direction: -1 | 1) => {
    if (!story) return;
    const idx = slides.findIndex((s) => s.id === slideId);
    const newIdx = idx + direction;
    if (idx < 0 || newIdx < 0 || newIdx >= slides.length) return;
    const reordered = [...slides];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setSlides(reordered);
    try {
      await storiesAdminService.reorderSlides(
        story.id,
        reordered.map((s) => s.id),
      );
    } catch (e: any) {
      toast.error('Не удалось сохранить порядок');
      loadStory();
    }
  };

  const statusBadge = useMemo(() => {
    if (!story) return null;
    const map: Record<StoryStatus, string> = {
      draft: 'bg-gray-200 text-gray-800',
      published: 'bg-green-100 text-green-800',
      archived: 'bg-amber-100 text-amber-800',
    };
    const label: Record<StoryStatus, string> = {
      draft: 'Черновик',
      published: 'Опубликовано',
      archived: 'В архиве',
    };
    return (
      <span
        className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${map[story.status]}`}
      >
        {label[story.status]}
      </span>
    );
  }, [story]);

  if (loading || !story) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Link to="/stories" className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
          К списку
        </Link>
        {statusBadge}
        <div className="flex-1" />
        <div className="flex gap-2">
          {story.status !== 'published' && (
            <button
              type="button"
              onClick={handlePublish}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              <Send className="h-4 w-4" />
              Опубликовать
            </button>
          )}
          {story.status === 'published' && (
            <button
              type="button"
              onClick={handleArchive}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
            >
              <Archive className="h-4 w-4" />В архив
            </button>
          )}
          <button
            type="button"
            onClick={handleDeleteStory}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
          >
            <Trash2 className="h-4 w-4" />
            Удалить
          </button>
        </div>
      </div>

      {/* Header — title + cover preview */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-4">
        <StoryCardPreview
          title={story.title}
          coverEmoji={story.cover_emoji}
          coverGradient={story.cover_gradient}
          cardTextColor={story.card_text_color}
          status={story.status}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={story.title}
            onChange={(e) => updateStoryLocal({ title: e.target.value })}
            className="w-full text-2xl font-bold text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-blue-500 focus:outline-none focus:ring-0 py-1"
            placeholder="Заголовок истории"
          />
          <div className="text-xs text-gray-500 font-mono mt-1">/{story.slug}</div>
          <div className="text-xs text-gray-500 mt-1">
            ID: {story.id} · слайдов: {slides.length}
          </div>
        </div>
        {storyDirty && (
          <button
            type="button"
            onClick={handleSaveStory}
            disabled={savingStory}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {savingStory ? 'Сохранение...' : 'Сохранить'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab('content')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
            tab === 'content'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <LayoutGrid className="h-4 w-4" />
          Контент и слайды
        </button>
        <button
          type="button"
          onClick={() => setTab('meta')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
            tab === 'meta'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sliders className="h-4 w-4" />
          Карточка и настройки
        </button>
        <button
          type="button"
          onClick={() => setTab('analytics')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
            tab === 'analytics'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <BarChart3 className="h-4 w-4" />
          Аналитика
        </button>
      </div>

      {tab === 'content' && (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          {/* Slide list */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 h-fit">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-gray-700">Слайды ({slides.length})</div>
              <button
                type="button"
                onClick={handleAddSlide}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Добавить
              </button>
            </div>
            {slides.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                Пока нет слайдов.
                <br />
                Добавьте первый.
              </div>
            ) : (
              <div className="space-y-2">
                {slides.map((s, idx) => {
                  const isActive = s.id === selectedSlideId;
                  return (
                    <div
                      key={s.id}
                      className={`relative rounded-lg border-2 p-2 cursor-pointer transition-colors ${
                        isActive
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={() => handleSelectSlide(s.id)}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className="h-12 w-9 rounded flex-shrink-0"
                          style={{ background: s.background_gradient }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500">
                            #{idx + 1} · {((s.duration_ms || 5000) / 1000).toFixed(1)}s
                          </div>
                          {s.eyebrow && (
                            <div className="text-[10px] text-gray-500 uppercase truncate">
                              {s.eyebrow}
                            </div>
                          )}
                          <div className="text-sm font-medium text-gray-900 line-clamp-2">
                            {s.headline}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveSlide(s.id, -1);
                          }}
                          disabled={idx === 0}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                          title="Выше"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveSlide(s.id, 1);
                          }}
                          disabled={idx === slides.length - 1}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
                          title="Ниже"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSlide(s.id);
                          }}
                          className="p-1 rounded text-red-600 hover:bg-red-50"
                          title="Удалить"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Slide editor */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[600px]">
            {editedSlide && story ? (
              <StorySlideEditor
                slide={editedSlide}
                totalSlides={slides.length}
                slideIndex={slides.findIndex((s) => s.id === editedSlide.id)}
                onChange={handleSlideChange}
                onSave={handleSaveSlide}
                onDelete={() => handleDeleteSlide(editedSlide.id)}
                saving={savingSlide}
                dirty={slideDirty}
                availableVars={availableVarsForSlide}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="text-gray-400 text-3xl mb-2">🎬</div>
                <div className="text-gray-700 font-medium mb-1">Выберите или создайте слайд</div>
                <p className="text-sm text-gray-500 mb-4">
                  История без слайдов не будет показана пользователям.
                </p>
                <button
                  type="button"
                  onClick={handleAddSlide}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  <Plus className="h-5 w-5" />
                  Добавить первый слайд
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'meta' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-3xl space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
            <input
              type="text"
              value={story.slug}
              onChange={(e) => updateStoryLocal({ slug: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Используется во внутренней аналитике и ссылках.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cover emoji (опционально)
            </label>
            <input
              type="text"
              value={story.cover_emoji ?? ''}
              onChange={(e) => updateStoryLocal({ cover_emoji: e.target.value || null })}
              maxLength={4}
              placeholder="🎯"
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-2xl text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Градиент обложки
            </label>
            <div className="flex gap-2 flex-wrap">
              {GRADIENT_PRESETS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => updateStoryLocal({ cover_gradient: g.value })}
                  className={`h-12 w-20 rounded-lg border-2 ${
                    story.cover_gradient === g.value
                      ? 'border-blue-500 scale-105'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                  style={{ background: g.value }}
                  title={g.label}
                />
              ))}
            </div>
            <input
              type="text"
              value={story.cover_gradient}
              onChange={(e) => updateStoryLocal({ cover_gradient: e.target.value })}
              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Цвет текста на карточке
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={story.card_text_color || '#FFFFFF'}
                onChange={(e) => updateStoryLocal({ card_text_color: e.target.value })}
                className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={story.card_text_color}
                onChange={(e) => updateStoryLocal({ card_text_color: e.target.value })}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Аудитория</label>
            <select
              value={story.target_audience}
              onChange={(e) =>
                updateStoryLocal({ target_audience: e.target.value as TargetAudience })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Все пользователи</option>
              <option value="free">Только Free</option>
              <option value="paid">Только Paid</option>
              <option value="admin">Только админы</option>
            </select>
          </div>

          {/* Scheduling: отложенный публиш + срок жизни (для announcement-сторис). */}
          <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Планирование</h3>
              <span className="text-xs text-gray-500">— когда показывать, до какой даты</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Публикация с
                <span className="text-gray-500 font-normal"> (пусто = сразу после status=published)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={story.published_at ? story.published_at.slice(0, 16) : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    // datetime-local → ISO (UTC). Пустая строка → null.
                    updateStoryLocal({ published_at: v ? new Date(v).toISOString() : null });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {story.published_at && (
                  <button
                    type="button"
                    onClick={() => updateStoryLocal({ published_at: null })}
                    className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Очистить дату публикации"
                  >
                    Сбросить
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Для системных сторис: публикация навсегда. Для announcement: задайте будущую дату,
                чтобы сторис появился отложенно.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Истекает
                <span className="text-gray-500 font-normal"> (пусто = показывать бессрочно)</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="datetime-local"
                  value={story.expires_at ? story.expires_at.slice(0, 16) : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateStoryLocal({ expires_at: v ? new Date(v).toISOString() : null });
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    // «Только сегодня» = следующая полночь МСК.
                    const now = new Date();
                    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
                    updateStoryLocal({ expires_at: tomorrow.toISOString() });
                  }}
                  className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  title="Истекает в полночь следующего дня"
                >
                  Только сегодня
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 7);
                    updateStoryLocal({ expires_at: d.toISOString() });
                  }}
                  className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  title="Истекает через 7 дней"
                >
                  +7 дней
                </button>
                {story.expires_at && (
                  <button
                    type="button"
                    onClick={() => updateStoryLocal({ expires_at: null })}
                    className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Сбросить дату истечения"
                  >
                    Сбросить
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Для системных сторис — оставьте пусто. Для announcement — выставьте дату, чтобы
                сторис автоматически исчез у юзеров.
              </p>
            </div>
          </div>

          {/* Dynamic-system-stories block */}
          <div className="border-t border-gray-200 pt-4 mt-2 space-y-4">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Динамические данные</h3>
              <span className="text-xs text-gray-500">— только для системных сторисов</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Тип</label>
              <select
                value={story.kind || 'static'}
                onChange={(e) => updateStoryLocal({ kind: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="static">Статический (как обычно)</option>
                <option value="system">Системный (рендер с подстановкой данных)</option>
              </select>
            </div>

            {story.kind === 'system' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Источники данных
                  </label>
                  {varsRegistry?.sources.map((src) => (
                    <label key={src.id} className="flex items-start gap-2 py-1">
                      <input
                        type="checkbox"
                        checked={(story.data_sources || []).includes(src.id)}
                        onChange={(e) => {
                          const cur = new Set(story.data_sources || []);
                          if (e.target.checked) cur.add(src.id);
                          else cur.delete(src.id);
                          updateStoryLocal({ data_sources: Array.from(cur) });
                        }}
                      />
                      <span className="text-sm">
                        <span className="font-medium">{src.label}</span>
                        <span className="text-gray-500"> ({src.id}, {src.variables.length} переменных
                          {src.itemVars ? `, ${src.itemVars.length} item-vars` : ''})
                        </span>
                      </span>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refresh strategy
                  </label>
                  <select
                    value={story.refresh_strategy || 'daily'}
                    onChange={(e) => updateStoryLocal({ refresh_strategy: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {varsRegistry?.refreshStrategies.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preset (опционально)
                  </label>
                  <select
                    value={story.preset_id || ''}
                    onChange={(e) => updateStoryLocal({ preset_id: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">— нет —</option>
                    {varsRegistry?.presets.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                {/* Доступные переменные — справка */}
                {(story.data_sources || []).length > 0 && varsRegistry && (
                  <div className="bg-gray-50 rounded-lg p-3 text-xs">
                    <div className="font-semibold mb-1 text-gray-700">
                      Доступные переменные (вставляйте в текст слайдов как <code>{`{{path}}`}</code>):
                    </div>
                    <div className="space-y-1 max-h-48 overflow-auto">
                      {varsRegistry.sources
                        .filter((s) => (story.data_sources || []).includes(s.id))
                        .flatMap((s) => [...s.variables, ...(s.itemVars || [])])
                        .map((v) => (
                          <div key={v.path} className="font-mono">
                            <span className="text-blue-600">{`{{${v.path}}}`}</span>
                            <span className="text-gray-500"> — {v.label} (пример: {v.example})</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSaveStory}
              disabled={savingStory || !storyDirty}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              <Save className="h-5 w-5" />
              {savingStory ? 'Сохранение...' : storyDirty ? 'Сохранить' : 'Сохранено'}
            </button>
          </div>
        </div>
      )}

      {tab === 'analytics' && (
        <StoryAnalyticsPanel story={story} stats={stats} loading={statsLoading} />
      )}
    </div>
  );
};

export default StoryEditPage;
